import React, { useState } from 'react';
import useSeo from '../lib/useSeo';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import { useToast } from '../hooks/use-toast';
import { Mail, MapPin, Clock, CheckCircle2, AlertCircle } from 'lucide-react';
import { track, events, emailDomain } from '../lib/analytics';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Material Kit Pro Contact-Us hero image (city skyline).
const HERO_IMG =
  'https://images.unsplash.com/photo-1519501025264-65ba15a82390?auto=format&fit=crop&w=2000&q=80';

const Field = ({ label, active, children }) => (
  <label className="block">
    <span
      className={`block text-[11px] font-bold uppercase tracking-[0.14em] transition-colors ${
        active ? 'text-brand-blue' : 'text-body-muted'
      }`}
    >
      {label}
    </span>
    {children}
  </label>
);

const Contact = () => {
  useSeo({
    title: 'Contact 5DBS — Book a 15-minute revenue call',
    description: 'Talk to a 5DBS revenue expert. 15 minutes, no deck, no pitch — just a clear read on where your next audience revenue line is.',
    path: '/contact',
  });
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [focus, setFocus] = useState({ name: false, email: false, message: false });
  const [submitting, setSubmitting] = useState(false);
  // 'idle' | 'success' | 'error'
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const inputCls = (active) =>
    `mt-1 w-full h-11 bg-transparent border-b-2 text-[15px] text-navy placeholder:text-body-muted focus:outline-none transition-colors ${
      active ? 'border-brand-blue' : 'border-slate-200'
    }`;

  const submit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const email = form.email.trim();
    const message = form.message.trim();
    setErrorMsg('');
    if (!name || !email || !message) {
      toast({ title: 'Please fill in all fields' });
      return;
    }
    if (!EMAIL_RE.test(email)) {
      toast({ title: 'Please enter a valid work email' });
      return;
    }
    setSubmitting(true);
    setStatus('idle');
    try {
      await axios.post(`${API_BASE}/leads`, {
        name,
        email,
        source: 'contact',
        gdpr_consent: false,
        message,
      });
      track(events.CONTACT_SUBMIT, { email_domain: emailDomain(email) });
      setForm({ name: '', email: '', message: '' });
      setStatus('success');
      toast({
        title: 'Message received',
        description: 'A member of the 5DBS team will reply within one business day.',
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const description =
        typeof detail === 'string'
          ? detail
          : 'Please try again in a moment or email connect@fivedbs.com.';
      setStatus('error');
      setErrorMsg(description);
      toast({ title: 'Something went wrong', description });
    } finally {
      setSubmitting(false);
    }
  };

  const sendAnother = () => {
    setStatus('idle');
    setErrorMsg('');
  };

  return (
    <div className="min-h-screen bg-white font-sans text-navy">
      <Navbar />

      {/* Hero + floating form card (Material Kit Pro Contact-Us pattern) */}
      <section className="relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/85 via-navy-deep/70 to-navy-deep/85" aria-hidden />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-8 pt-32 pb-24 md:pt-40 md:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            {/* Left column: hero copy */}
            <div className="lg:col-span-5 text-white">
              <p className="text-brand-blue-light text-[12px] font-bold tracking-[0.16em] uppercase mb-4">
                Get in touch
              </p>
              <h1 className="font-black tracking-[-0.01em] leading-[1.05] text-[40px] sm:text-[48px] lg:text-[56px]">
                Let&apos;s build your next revenue line.
              </h1>
              <p className="mt-5 text-slate-200 text-[16px] leading-[1.7] max-w-lg">
                For further questions, including partnership opportunities, please
                email <a href="mailto:connect@fivedbs.com" className="text-brand-blue-light font-semibold hover:underline">connect@fivedbs.com</a> or drop
                us a note below. We reply within one business day.
              </p>

              <ul className="mt-10 space-y-5">
                <li className="flex items-start gap-4">
                  <span className="mt-1 h-9 w-9 rounded-lg bg-brand-blue/20 border border-brand-blue/40 inline-flex items-center justify-center shrink-0">
                    <Mail className="h-4 w-4 text-brand-blue-light" />
                  </span>
                  <div>
                    <p className="text-white text-[13px] font-bold uppercase tracking-[0.1em]">Email</p>
                    <a href="mailto:connect@fivedbs.com" className="text-slate-300 text-[15px] hover:text-white">
                      connect@fivedbs.com
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="mt-1 h-9 w-9 rounded-lg bg-brand-blue/20 border border-brand-blue/40 inline-flex items-center justify-center shrink-0">
                    <MapPin className="h-4 w-4 text-brand-blue-light" />
                  </span>
                  <div>
                    <p className="text-white text-[13px] font-bold uppercase tracking-[0.1em]">Office</p>
                    <p className="text-slate-300 text-[15px] leading-[1.5]">
                      60 St Martins Lane, Covent Garden<br />London, WC2N 4JS, United Kingdom
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="mt-1 h-9 w-9 rounded-lg bg-brand-blue/20 border border-brand-blue/40 inline-flex items-center justify-center shrink-0">
                    <Clock className="h-4 w-4 text-brand-blue-light" />
                  </span>
                  <div>
                    <p className="text-white text-[13px] font-bold uppercase tracking-[0.1em]">Response time</p>
                    <p className="text-slate-300 text-[15px]">Within 1 business day, Mon&ndash;Fri</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* Right column: floating form card */}
            <div className="lg:col-span-7">
              <div className="bg-white rounded-2xl shadow-mk-lg p-8 sm:p-10">
                <h2 className="text-navy font-black tracking-[-0.005em] text-[26px] sm:text-[30px]">
                  Contact us
                </h2>
                <p className="mt-2 text-body-muted text-[14.5px] leading-[1.6]">
                  Tell us where you are on your revenue journey and what you&apos;d like to unlock.
                </p>

                {status === 'success' ? (
                  <div
                    role="status"
                    data-testid="contact-success-panel"
                    className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50 p-6 sm:p-7"
                  >
                    <div className="flex items-start gap-4">
                      <span className="mt-0.5 h-10 w-10 rounded-full bg-emerald-500/10 border border-emerald-500/30 inline-flex items-center justify-center shrink-0">
                        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-emerald-800 text-[11px] font-bold uppercase tracking-[0.16em]">
                          Message sent
                        </p>
                        <h3 className="mt-1 text-navy font-black text-[20px] leading-tight">
                          Thanks &mdash; we&apos;ve got your note.
                        </h3>
                        <p className="mt-2 text-body-muted text-[14.5px] leading-[1.65]">
                          A member of the 5DBS team will reply within one business day, Monday to Friday.
                          Meanwhile, feel free to explore our products or book a 15-minute call.
                        </p>
                        <div className="mt-5 flex flex-wrap items-center gap-3">
                          <button
                            type="button"
                            data-testid="contact-send-another-button"
                            onClick={sendAnother}
                            className="inline-flex items-center justify-center h-10 px-5 rounded-md bg-brand-pink hover:bg-brand-pink/90 text-white text-[12px] font-bold uppercase tracking-[0.08em] shadow-mk hover:shadow-mk-lg hover:-translate-y-0.5 transition-all duration-300"
                          >
                            Send another message
                          </button>
                          <Link
                            to="/"
                            className="inline-flex items-center justify-center h-10 px-5 rounded-md border border-slate-300 text-navy hover:border-brand-blue hover:text-brand-blue text-[12px] font-bold uppercase tracking-[0.08em] transition-colors"
                          >
                            Back to home
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={submit} className="mt-8 space-y-6" data-testid="contact-form">
                    {status === 'error' && (
                      <div
                        role="alert"
                        data-testid="contact-error-panel"
                        className="rounded-xl border border-rose-200 bg-rose-50 p-4 flex items-start gap-3"
                      >
                        <AlertCircle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-rose-800 text-[11px] font-bold uppercase tracking-[0.16em]">
                            Message not sent
                          </p>
                          <p className="mt-1 text-navy text-[14px] leading-[1.55]">{errorMsg}</p>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <Field label="Your name" active={focus.name}>
                        <input
                          placeholder="John Smith"
                          data-testid="contact-name-input"
                          value={form.name}
                          onFocus={() => setFocus((f) => ({ ...f, name: true }))}
                          onBlur={() => setFocus((f) => ({ ...f, name: false }))}
                          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                          className={inputCls(focus.name)}
                        />
                      </Field>
                      <Field label="Email address" active={focus.email}>
                        <input
                          placeholder="you@company.com"
                          type="email"
                          data-testid="contact-email-input"
                          value={form.email}
                          onFocus={() => setFocus((f) => ({ ...f, email: true }))}
                          onBlur={() => setFocus((f) => ({ ...f, email: false }))}
                          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                          className={inputCls(focus.email)}
                        />
                      </Field>
                    </div>

                    <Field label="Your message" active={focus.message}>
                      <textarea
                        rows={5}
                        placeholder="Describe your goal and where you&apos;re starting from…"
                        data-testid="contact-message-input"
                        value={form.message}
                        onFocus={() => setFocus((f) => ({ ...f, message: true }))}
                        onBlur={() => setFocus((f) => ({ ...f, message: false }))}
                        onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                        className={`mt-2 w-full bg-transparent border-b-2 py-2 text-[15px] text-navy placeholder:text-body-muted focus:outline-none transition-colors resize-none ${
                          focus.message ? 'border-brand-blue' : 'border-slate-200'
                        }`}
                      />
                    </Field>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                      <p className="text-body-muted text-[12.5px] leading-[1.55] max-w-md">
                        By sending this message you agree to our{' '}
                        <Link to="/privacy" className="text-brand-blue font-semibold hover:underline">
                          Privacy Policy
                        </Link>{' '}
                        and{' '}
                        <Link to="/terms" className="text-brand-blue font-semibold hover:underline">
                          Terms of Use
                        </Link>.
                      </p>
                      <button
                        type="submit"
                        data-testid="contact-submit-button"
                        disabled={submitting}
                        className="inline-flex items-center justify-center h-11 px-7 rounded-md bg-brand-pink hover:bg-brand-pink/90 text-white text-[13px] font-bold uppercase tracking-[0.08em] shadow-mk hover:shadow-mk-lg hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60 shrink-0"
                      >
                        {submitting ? 'Sending…' : 'Send message'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
